import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { DEFAULT_PACKAGE, PRICE_TIERS, pricePackage, money } from "@/lib/landingSim";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg } from "@/components/webadvisor/LeadDetails";

const blank = { name: "", project_fee: "", deposit_pct: 50, package: null, terms: [], admin_notes: "", extra_items: [] };
const F = ({ label, children }) => <div><Label className="text-xs">{label}</Label>{children}</div>;

export const TemplateForm = ({ initial, onSaved, onCancel }) => {
  const [t, setT] = useState({ ...blank, ...(initial || {}) });
  const [terms, setTerms] = useState((initial?.terms || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const pkg = t.package;
  const setPkg = (k, v) => setT({ ...t, package: { ...pkg, [k]: v } });
  const pages = pkg ? pricePackage({ ...pkg, page_count: Number(pkg.page_count) || 0 }).total : 0;

  const save = async () => {
    setBusy(true);
    const body = { ...t, project_fee: t.project_fee === "" ? null : Number(t.project_fee), deposit_pct: Number(t.deposit_pct), terms: terms.split("\n"),
      package: pkg ? { ...pkg, page_count: Number(pkg.page_count), custom_unit_price: Number(pkg.custom_unit_price), discount_pct: Number(pkg.discount_pct) } : null };
    try {
      const { data } = initial?.id ? await api.put(`/api/webadvisor/templates/${initial.id}`, body) : await api.post("/api/webadvisor/templates", body);
      toast.success("Template saved"); onSaved(data);
    } catch (e) { toast.error(errMsg(e, "Couldn't save the template")); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 border border-slate-200 rounded-xl p-4 bg-slate-50" data-testid="template-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <F label="Template name"><Input value={t.name} onChange={e => setT({ ...t, name: e.target.value })} placeholder="e.g. Starter 20 pages" data-testid="template-name-input" /></F>
        <F label="Project price ($)"><Input type="number" min="0" value={t.project_fee ?? ""} onChange={e => setT({ ...t, project_fee: e.target.value })} data-testid="template-fee-input" /></F>
        <F label="Deposit %"><Input type="number" min="10" max="100" value={t.deposit_pct} onChange={e => setT({ ...t, deposit_pct: e.target.value })} data-testid="template-deposit-input" /></F>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={!!pkg} onCheckedChange={v => setT({ ...t, package: v ? { ...DEFAULT_PACKAGE } : null })} data-testid="template-pkg-switch" />Include landing pages</label>
      {pkg && (
        <div className="grid sm:grid-cols-4 gap-3">
          <F label="Pages"><Input type="number" min="1" max="1000" value={pkg.page_count} onChange={e => setPkg("page_count", e.target.value)} data-testid="template-pages-input" /></F>
          <F label="Price tier">
            <select value={pkg.price_tier} onChange={e => setPkg("price_tier", e.target.value)} className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="template-tier-select">
              {Object.entries(PRICE_TIERS).map(([k, v]) => <option key={k} value={k}>{k} ({money(v)}/page)</option>)}<option value="custom">custom</option>
            </select>
          </F>
          {pkg.price_tier === "custom" && <F label="Price per page ($)"><Input type="number" min="0" value={pkg.custom_unit_price} onChange={e => setPkg("custom_unit_price", e.target.value)} data-testid="template-unit-input" /></F>}
          <F label="Discount %"><Input type="number" min="0" max="100" value={pkg.discount_pct} onChange={e => setPkg("discount_pct", e.target.value)} data-testid="template-discount-input" /></F>
        </div>
      )}
      <F label="Contract terms (one per line — leave empty to let AI write them)"><Textarea rows={4} value={terms} onChange={e => setTerms(e.target.value)} data-testid="template-terms-input" /></F>
      <F label="Instructions for the AI contract writer (optional)"><Textarea rows={2} value={t.admin_notes} onChange={e => setT({ ...t, admin_notes: e.target.value })} data-testid="template-notes-input" /></F>
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-slate-700 flex-1" data-testid="template-form-total">Estimated quote <b>{money((Number(t.project_fee) || 0) + pages)}</b>{pkg ? ` (pages ${money(pages)})` : ""}</p>
        {onCancel && <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-900" data-testid="template-cancel-btn">Cancel</button>}
        <button onClick={save} disabled={busy || !t.name.trim()} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50" data-testid="template-save-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save template
        </button>
      </div>
    </div>
  );
};
